import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link, Navigate, Outlet, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Bell,
  CalendarDays,
  CircleUserRound,
  Home,
  Languages,
  Menu,
  MessageCircle,
  ReceiptText,
  Compass,
  TicketPercent,
  X,
  type LucideIcon,
} from "lucide-react";
import { changeLanguage } from "@/services/slices/setting.slice";
import AppLogo from "@/components/AppLogo";

type StudentMenuItem = { to: string; label: string; icon: LucideIcon };

const NAV_ITEMS = [
  { to: "/student/home", label: "Home", icon: Home },
  { to: "/student/calendar", label: "Calendar", icon: CalendarDays },
  { to: "/student/activities", label: "Activities", icon: Compass },
  { to: "/student/redeem", label: "Coupons & Sponsors", icon: TicketPercent },
  { to: "/student/redeemed", label: "My Redemptions", icon: ReceiptText },
  { to: "/student/account", label: "Account", icon: CircleUserRound },
];

const ICON_NAV_ITEMS = [
  { to: "/student/notifications", label: "Notifications", icon: Bell },
  { to: "/student/chat", label: "Chat", icon: MessageCircle },
];

/** Drawer order: the section links, the two icon links, then Account. */
const MOBILE_MENU_ITEMS: StudentMenuItem[] = [
  ...NAV_ITEMS.filter((item) => item.to !== "/student/account"),
  ...ICON_NAV_ITEMS,
  ...NAV_ITEMS.filter((item) => item.to === "/student/account"),
];

const isActivePath = (pathname: string, to: string) =>
  pathname === to || pathname.startsWith(`${to}/`);

/**
 * Student shell: guards `/student/*` routes — only a logged-in student may
 * enter. Renders the top navbar + content outlet.
 */
const StudentLayout: React.FC = () => {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const dispatch = useDispatch();
  const { loggedIn, user_auth } = useSelector((state: any) => state.auth);
  const language = useSelector((state: any) => state.setting.language);
  const [menuOpen, setMenuOpen] = useState(false);

  const toggleLanguage = () => {
    dispatch(
      changeLanguage({ language: language === "zh_hant" ? "en" : "zh_hant" }),
    );
  };

  useEffect(() => {
    if (!menuOpen) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") setMenuOpen(false);
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [menuOpen]);

  useEffect(() => {
    setMenuOpen(false);
  }, [pathname]);

  if (!loggedIn || (user_auth !== "student" && user_auth !== "organizer")) {
    return <Navigate to="/login" replace />;
  }

  return (
    <div className="flex min-h-screen flex-col bg-[#f5f5f5]">
      <header className="sticky top-0 z-40 border-b border-gray-100 bg-white">
        <div className="relative mx-auto flex h-16 w-full max-w-7xl items-center justify-between gap-3 px-4 sm:px-6">
          <Link to="/student/home" className="flex shrink-0 items-center gap-2">
            <AppLogo size={36} />
            <span className="hidden text-xl font-bold text-[#333] sm:inline">
              Uninfo<span className="text-[#ffa217]">HK</span>
            </span>
          </Link>

          <nav className="hidden min-w-0 flex-1 items-center justify-end gap-1 xl:flex">
            {NAV_ITEMS.map((item) => {
              const Icon = item.icon;
              const isActive = isActivePath(pathname, item.to);
              return (
                <Link
                  key={item.to}
                  to={item.to}
                  className={`flex items-center gap-1.5 whitespace-nowrap rounded-lg px-3 py-2 text-sm font-medium transition ${
                    isActive
                      ? "bg-[#fff3e0] font-semibold text-[#ffa217]"
                      : "text-gray-600 hover:bg-gray-50 hover:text-[#ffa217]"
                  }`}
                >
                  <Icon size={16} className="shrink-0" />
                  {t(item.label)}
                </Link>
              );
            })}

            {ICON_NAV_ITEMS.map((item) => {
              const Icon = item.icon;
              const isActive = isActivePath(pathname, item.to);
              return (
                <Link
                  key={item.to}
                  to={item.to}
                  aria-label={t(item.label)}
                  title={t(item.label)}
                  className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-lg transition ${
                    isActive
                      ? "bg-[#fff3e0] text-[#ffa217]"
                      : "text-[#ffa217] hover:bg-[#fff3e0]"
                  }`}
                >
                  <Icon size={19} />
                </Link>
              );
            })}

            <button
              type="button"
              onClick={toggleLanguage}
              aria-label={t("Language")}
              className="flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-lg px-2.5 py-2 text-sm font-semibold text-gray-600 transition hover:bg-[#fff3e0] hover:text-[#ffa217]"
            >
              <Languages size={16} className="shrink-0 text-[#ffa217]" />
              {language === "zh_hant" ? "EN" : "繁體"}
            </button>
          </nav>

          <div className="flex shrink-0 items-center gap-2 xl:hidden">
            <button
              type="button"
              onClick={() => setMenuOpen((prev) => !prev)}
              aria-label={t("Menu")}
              title={t("Menu")}
              aria-expanded={menuOpen}
              aria-haspopup="menu"
              className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg text-gray-600 transition hover:bg-gray-50 hover:text-[#ffa217]"
            >
              {menuOpen ? <X size={20} /> : <Menu size={20} />}
            </button>
          </div>

          {menuOpen && (
            <div
              role="menu"
              aria-label={t("Menu")}
              className="absolute right-3 top-full z-50 w-56 rounded-b-2xl border border-t-0 border-gray-100 bg-white py-2 shadow-xl xl:hidden"
            >
              {MOBILE_MENU_ITEMS.map((item) => {
                const Icon = item.icon;
                const isActive = isActivePath(pathname, item.to);
                return (
                  <Link
                    key={item.to}
                    to={item.to}
                    role="menuitem"
                    onClick={() => setMenuOpen(false)}
                    className={`flex items-center gap-2.5 px-4 py-2.5 text-sm font-medium transition ${
                      isActive
                        ? "bg-[#fff3e0] font-semibold text-[#ffa217]"
                        : "text-gray-600 hover:bg-gray-50"
                    }`}
                  >
                    <Icon size={17} className="shrink-0 text-[#ffa217]" />
                    {t(item.label)}
                  </Link>
                );
              })}

              <button
                type="button"
                role="menuitem"
                onClick={toggleLanguage}
                className="mt-1 flex w-full items-center gap-2.5 border-t border-gray-100 px-4 py-2.5 text-sm font-medium text-gray-600 transition hover:bg-gray-50"
              >
                <Languages size={17} className="shrink-0 text-[#ffa217]" />
                <span className="ml-auto text-sm font-semibold text-[#ffa217]">
                  {language === "zh_hant" ? "EN" : "繁體"}
                </span>
              </button>
            </div>
          )}
        </div>
      </header>

      {menuOpen && (
        <div
          className="fixed inset-0 z-30 xl:hidden"
          aria-hidden="true"
          onClick={() => setMenuOpen(false)}
        />
      )}

      <main className="flex-1">
        <Outlet />
      </main>
    </div>
  );
};

export default StudentLayout;
